import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {ReturnType<typeof setTimeout>[]} */
  #cartCountSyncTimers = [];

  #cartCountSyncGeneration = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    this.#clearCartCountSyncTimers();
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);

    // Promotion apps can update the cart again after the theme's response. For
    // example, a free gift is removed asynchronously when its qualifying watch
    // is removed. Reconcile twice so the badge reflects Shopify's final cart.
    if (event.detail.data?.source === 'cart-items-component') {
      this.#scheduleCartCountSync();
    }
  };

  #scheduleCartCountSync = () => {
    this.#clearCartCountSyncTimers();
    const generation = ++this.#cartCountSyncGeneration;

    for (const delay of [500, 2000]) {
      const timer = setTimeout(() => this.#syncCartCount(generation), delay);
      this.#cartCountSyncTimers.push(timer);
    }
  };

  #clearCartCountSyncTimers = () => {
    for (const timer of this.#cartCountSyncTimers) clearTimeout(timer);
    this.#cartCountSyncTimers = [];
  };

  /** @param {number} generation */
  #syncCartCount = async (generation) => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok || generation !== this.#cartCountSyncGeneration) return;

      const cart = await response.json();
      const itemCount = Number(cart.item_count);

      if (Number.isInteger(itemCount) && itemCount >= 0 && itemCount !== this.currentCartCount) {
        this.renderCartBubble(itemCount, false, false);
      }
    } catch (_) {
      // A cart count refresh is best-effort; the original update remains valid.
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count
    const currentCount = Number.isInteger(this.currentCartCount) ? this.currentCartCount : 0;
    const nextItemCount = comingFromProductForm ? currentCount + itemCount : itemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', nextItemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextItemCount === 0);

    this.currentCartCount = nextItemCount;
    this.#renderOtherCartBubbles(nextItemCount);

    this.classList.toggle('header-actions__cart-icon--has-cart', nextItemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || nextItemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /** @param {number} itemCount */
  #renderOtherCartBubbles = (itemCount) => {
    for (const count of document.querySelectorAll('[data-testid="cart-bubble"]')) {
      if (count === this.refs.cartBubbleCount) continue;

      count.textContent = itemCount < 100 ? String(itemCount) : '';
      count.classList.toggle('hidden', itemCount === 0);

      const bubble = count.closest('.cart-bubble');
      bubble?.classList.toggle('visually-hidden', itemCount === 0);

      const accessibleCount = bubble?.querySelector('.cart-bubble__text > .visually-hidden');
      if (accessibleCount) {
        accessibleCount.textContent = accessibleCount.textContent.replace(/:\s*\d+\s*$/, `: ${itemCount}`);
      }
    }
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
